/*
 * Responsive image helpers.
 *
 * Local images (/images/*.webp) have pre-generated width variants created at
 * build time: {name}-480.webp, {name}-800.webp, {name}-1200.webp.
 * Remote Pexels/Unsplash URLs support a `w=` query param, so variants are
 * derived by rewriting the URL.
 */

import IMAGE_DIMS from './imageDims.json';

const VARIANT_WIDTHS = [480, 800, 1200];

/** Common `sizes` presets keyed by layout role. */
export const SIZES = {
  full: '100vw',
  half: '(max-width: 1024px) 100vw, 50vw',
  third: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  quarter: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw',
  card: '(max-width: 640px) 100vw, 420px',
  thumb: '(max-width: 640px) 50vw, 260px',
};

/**
 * Build a srcSet string for a local or remote image.
 * Returns undefined for unsupported sources (svg, logos, data URIs) so the
 * <img> gracefully falls back to plain src.
 */
export function srcSetFor(src, originalWidth = 1600) {
  if (!src) return undefined;
  // Admin-uploaded media (FIX-02: Projects / Team / Hero-image slots) follows the exact same
  // {base}-{width}.webp convention as the build-time /images/ variants — backend/media.py
  // generates the same VARIANT_WIDTHS (480/800/1200), so this branch covers both.
  if ((src.startsWith('/images/') || src.startsWith('/api/media/')) && src.endsWith('.webp')) {
    const base = src.slice(0, -'.webp'.length);
    const variants = VARIANT_WIDTHS.filter((w) => w < originalWidth)
      .map((w) => `${base}-${w}.webp ${w}w`);
    return [...variants, `${src} ${originalWidth}w`].join(', ');
  }
  if (src.includes('images.pexels.com') || src.includes('images.unsplash.com')) {
    const rewrite = (w) => (
      /[?&]w=\d+/.test(src) ? src.replace(/([?&])w=\d+/, `$1w=${w}`) : `${src}${src.includes('?') ? '&' : '?'}w=${w}`
    );
    return [...VARIANT_WIDTHS, originalWidth]
      .filter((w, i, a) => a.indexOf(w) === i && w <= originalWidth)
      .map((w) => `${rewrite(w)} ${w}w`)
      .join(', ');
  }
  return undefined;
}

/* ------------------------------------------------------------------------------------------ */
/* Phone-portrait hero sources (mobile polish §4).                                              */
/* Hero <img>s are 100vw landscape sets; a 375x812 phone would take the 800w file and stretch  */
/* it ~3x into a portrait cover. Each hero therefore gets a portrait-cropped set served from a  */
/* <picture><source media="(max-width: 767px)"> — see HeroPicture in components/Shared.js.      */
/* ------------------------------------------------------------------------------------------ */
const PORTRAIT = [[750, 1600], [1080, 2300]];

/**
 * Portrait srcSet for a hero image, or undefined when none can be derived.
 *  - Local /images/{name}.webp → /images/{name}-portrait-750.webp, -1080.webp
 *    (generated by scripts/make_portrait_heroes.py, subject centred).
 *  - Unsplash / Pexels URLs → the CDN crops on the fly: fit=crop&w=750&h=1600 (centre crop).
 */
export function portraitSrcSet(src) {
  if (!src) return undefined;
  if (src.startsWith('/images/') && src.endsWith('.webp')) {
    const base = src.slice(0, -'.webp'.length);
    return PORTRAIT.map(([w]) => `${base}-portrait-${w}.webp ${w}w`).join(', ');
  }
  if (src.includes('images.unsplash.com') || src.includes('images.pexels.com')) {
    const withParam = (url, key, value) => (
      new RegExp(`[?&]${key}=`).test(url) ? url.replace(new RegExp(`([?&])${key}=[^&]*`), `$1${key}=${value}`) : `${url}${url.includes('?') ? '&' : '?'}${key}=${value}`
    );
    return PORTRAIT.map(([w, h]) => {
      let u = withParam(src, 'w', w);
      u = withParam(u, 'h', h);
      u = withParam(u, 'fit', 'crop');
      return `${u} ${w}w`;
    }).join(', ');
  }
  return undefined;
}

/**
 * Intrinsic width/height for an <img> (mobile polish §4: explicit dimensions so cards don't
 * jump as images load). Looked up from lib/imageDims.json, which scripts/make_portrait_heroes.py
 * regenerates from the files in public/ and a one-time fetch of each remote hero.
 * Spread onto the element: <img {...dims(src)} />. Returns {} for unknown sources.
 */
export function dims(src) {
  const d = src && IMAGE_DIMS[src];
  return d ? { width: d[0], height: d[1] } : {};
}
